import { useEffect, useState } from "react";
// "@/lib/api", NOT a relative "./api", and the difference matters under Jest: every suite
// that renders a storefront already shims `@/lib/api` virtually, while a relative import
// resolves the REAL module and drags in axios's ESM build, which CRA's Jest cannot parse.
// Same convention as components/checkout/PromoField.jsx.
import { api } from "@/lib/api";

/**
 * useCheckoutQuote — WHAT THIS PURCHASE WILL COST, as the server will charge it.
 *
 * THE RULE THIS HOOK EXISTS TO ENFORCE (BUG-048/BUG-054): no price arithmetic in
 * JavaScript. The tax is `platform_helpers.compute_tax_split`'s, the discount is
 * `modules.promos.resolve_discount`'s, and both are reached through the ONE preview
 * endpoint that runs the same engines the charge runs —
 * `GET /api/public/checkout-breakdown` (backend/platform_settings_routes.py). A page that
 * multiplied a rate by a price would be a second implementation of the money, and a
 * storefront quoting 100.00 while Stripe asks 114.98 is exactly what that produces.
 *
 * ADVISORY, NEVER A PROMISE. Every figure is re-decided at payment: a tax line can be
 * edited, a promo code can expire. That is why nothing here is cached beyond the current
 * inputs and why the storefront still lets the server refuse.
 *
 * `null` means NOT KNOWN — in flight, or the read failed — and the caller must then say
 * so (see PriceBreakdown), never present the pre-tax price as the total.
 *
 * @param {object}  args
 * @param {string}  args.serviceType   offering kind: booking | telehealth | course |
 *                                     program | product | giftcard | invoice
 * @param {string}  args.offeringType  the catalogue the item lives in (same vocabulary,
 *                                     minus invoice/giftcard) — naming it makes the quote
 *                                     honour that row's own tax treatment (an exempt
 *                                     service quotes no tax)
 * @param {string}  args.offeringId    the row's id
 * @param {number}  args.amountCents   the advertised price of the item
 * @param {string}  args.tenantSlug    whose practice is selling (the public endpoint is
 *                                     reachable from the platform host too, so the
 *                                     practice is named rather than inferred)
 * @param {string}  args.promoCode     a code the buyer has entered, or "" for none
 * @param {boolean} args.enabled       false suspends the read entirely (a free item, a
 *                                     per-minute consultation with no fixed price)
 * @returns {{quote: object|null, error: object|null, loading: boolean}}
 */
export default function useCheckoutQuote({
  serviceType,
  offeringType = "",
  offeringId = "",
  amountCents,
  tenantSlug,
  promoCode = "",
  enabled = true,
}) {
  const [state, setState] = useState({ quote: null, error: null, loading: false });
  const cents = Number(amountCents);
  const on = Boolean(enabled && serviceType && tenantSlug && Number.isFinite(cents) && cents > 0);

  useEffect(() => {
    if (!on) {
      setState({ quote: null, error: null, loading: false });
      return undefined;
    }
    let alive = true;
    setState((s) => ({ ...s, loading: true }));
    // Debounced: a promo field fires per keystroke in the pages that quote as the buyer
    // types, and one round trip per keystroke is a request storm for a number still
    // being typed.
    const id = setTimeout(async () => {
      const params = new URLSearchParams({
        service_type: serviceType,
        amount_cents: String(Math.round(cents)),
        tenant_slug: tenantSlug,
      });
      if (offeringType) params.set("offering_type", offeringType);
      if (offeringId) params.set("offering_id", offeringId);
      if (promoCode) params.set("promo_code", promoCode);
      try {
        const { data } = await api.get(`/public/checkout-breakdown?${params.toString()}`);
        if (alive) setState({ quote: data || null, error: null, loading: false });
      } catch (e) {
        // The refusal is KEPT, not discarded: a promo the server will not accept is
        // something the buyer must read before paying, and the caller renders it through
        // `checkoutErrorMessage` (the same stable-code map a refused checkout uses).
        if (alive) setState({ quote: null, error: e, loading: false });
      }
    }, 250);
    return () => { alive = false; clearTimeout(id); };
  }, [on, serviceType, offeringType, offeringId, cents, tenantSlug, promoCode]);

  return state;
}
